import { PlusOutlined } from '@ant-design/icons';
import { DrawerCloseButton, OrderServiceSelect } from '@components';
import { useTenantVat, useView } from '@hooks';
import { L } from '@lib';
import { CreateServiceOrder, Order } from '@services';
import { useStore } from '@stores';
import { Utils } from '@utils';
import { Button, Drawer, Space } from 'antd';
import { observer } from 'mobx-react';
import React, { useEffect, useState } from 'react';

interface IProps {
  open: boolean;
  loading?: boolean;
  order?: Order;
  onCancel: () => void;
  onSubmit: (serviceOrders: CreateServiceOrder[]) => Promise<void> | void;
}

const ReviseEstimateDrawer: React.FC<IProps> = observer(
  ({ open, loading, order, onCancel, onSubmit }) => {
    const tenantVat = useTenantVat();
    const { isMobile } = useView();
    const { orderStore, serviceStore } = useStore();
    const [workPickerOpen, setWorkPickerOpen] = useState(false);
    const totals = serviceStore.revisionPricingSummary;

    useEffect(() => {
      if (open && order) {
        serviceStore.initRevisionFromOrder(order, tenantVat);
      }
      if (!open) {
        serviceStore.clearRevisionSelectedServices();
      }
    }, [open, order, tenantVat, serviceStore]);

    const canSubmit = serviceStore.revisionSelectedServices.length > 0;

    const handleSave = async () => {
      if (!order) return;
      const payload = orderStore.buildServiceOrders(
        {
          selectedServices: serviceStore.revisionSelectedServices,
          selectedServiceParts: serviceStore.revisionServiceParts,
          tenantVat: serviceStore.tenantVat,
        },
        order.id
      ) as unknown as CreateServiceOrder[];
      // The parent handler re-hydrates the Job details tab after a successful revise.
      await onSubmit(payload);
    };

    const handleClose = () => {
      // The picker's open flag lives here, not in the destroyed body - reset it or the next open
      // of the drawer would start with the picker already up.
      setWorkPickerOpen(false);
      serviceStore.clearRevisionSelectedServices();
      onCancel();
    };

    return (
      <Drawer
        title={L('Revise Estimate')}
        placement="right"
        width={isMobile ? '100%' : '90vw'}
        open={open}
        onClose={handleClose}
        destroyOnHidden
        closable={false}
        // The same shape as the job card: Add work in the header, the list as the body, totals in
        // the footer beside the actions - the inline service select and header strip are gone.
        extra={
          <Space>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              style={isMobile ? undefined : { minWidth: 200 }}
              onClick={() => setWorkPickerOpen(true)}
            >
              {L('Add work')}
            </Button>
            <DrawerCloseButton onClick={handleClose} />
          </Space>
        }
        footer={
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 12,
              flexWrap: 'wrap',
            }}
          >
            <div
              style={{
                display: 'flex',
                gap: 16,
                fontSize: 13,
                color: 'rgba(0,0,0,0.55)',
                alignItems: 'center',
                flexWrap: 'wrap',
              }}
            >
              <span>
                Labour: <strong>{Utils.formatCurrency(totals.labourPrice)}</strong>
              </span>
              <span>
                Parts: <strong>{Utils.formatCurrency(totals.partsPrice)}</strong>
              </span>
              <span>
                VAT: <strong>{Utils.formatCurrency(totals.vatAmount)}</strong>
              </span>
              <span
                style={{
                  fontWeight: 700,
                  color: '#1a3353',
                  background: '#e8f4fd',
                  padding: '4px 12px',
                  borderRadius: 4,
                }}
              >
                TOTAL: {Utils.formatCurrency(totals.totalPrice)}
              </span>
            </div>
            <Space>
              <Button onClick={handleClose}>{L('Cancel')}</Button>
              <Button type="primary" loading={loading} disabled={!canSubmit} onClick={handleSave}>
                {L('Save')}
              </Button>
            </Space>
          </div>
        }
      >
        <OrderServiceSelect
          variant="revision"
          atLeastOneService
          hideHeader
          pickerOpen={workPickerOpen}
          onPickerClose={() => setWorkPickerOpen(false)}
        />
      </Drawer>
    );
  }
);

export default ReviseEstimateDrawer;
